import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal } from 'react-bootstrap';
import { AiOutlineLineChart } from "react-icons/ai";
import DinamicTableAnalytics from './DinamicTableAnalytics';
import DinamicTableDetail from './DinamicTableDetail';
import CustomDatePicker from '../CustomDatePicker';
import Spinner from '../../utils/Spinner';
import ValidView from "../../../components/app/validView";
import { Chart } from "react-google-charts";
import { ExtractChartData, ChartPopup } from './Utils';
import { BodyViewsByPageTitle, BodyUsersByCity, BodySessionByChannel, BodyUsers30MinAgo, BodyViewsByUserSource, BodyViewsByPages } from './analyticsBodies';
import ModalDetail from './ModalDetail';

const GoogleAnalyticsReport = () => {
  const { allowWrite, allowView, userToken } = ValidView("Reports");
  const [isLoading, setIsLoading] = useState(true);
  const [timer, setTimer] = useState(null);

  const [responseViewsByPageTitle, setResponseViewsByPageTitle] = useState({});
  const [responseUsersByCity, setResponseUsersByCity] = useState({});
  const [responseSessionByChannel, setResponseSessionByChannel] = useState({});
  const [responseUsers30MinAgo, setResponseUsers30MinAgo] = useState({});
  const [responseViewsByUserSource, setResponseViewsByUserSource] = useState({});
  const [responseViewsByPages, setResponseViewsByPages] = useState({});

  const styles = { cardContainer: { maxHeight: '580px', overflowY: 'auto', marginBottom: '20px' } };

  const [dataChartViewsByClients, setDataChartViewsByClients] = useState([]);
  const [dataChartUsersByChannel, setDataChartUsersByChannel] = useState([]);
  const [dataChartViewsByUserSource, setDataChartViewsByUserSource] = useState([]);
  const [dataChartViewsByPages, setDataChartViewsByPages] = useState([]);
  const [dataChartUsersByCity, setDataChartUsersByCity] = useState([]);
  const [dataChartUsers30MinAgo, setDataChartUsers30MinAgo] = useState([]);

  const [showChartPopup, setShowChartPopup] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [pathClientDetail, setPathClientDetail] = useState('');
  const [selectedChartData, setSelectedChartData] = useState([]);
  const [chartPopupTitle, setChartPopupTitle] = useState('Chart');

  const currentDate = new Date();
  const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

  const [dates, setDates] = useState({
    startDate: new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 0, 0, 0, 0),
    endDate: new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 23, 59, 59, 999),
  });


  const handleChartButtonClick = (chartData, titleChartPopup) => {
    setSelectedChartData(chartData);
    setChartPopupTitle(titleChartPopup);
    setShowChartPopup(true);
  };


  const fetchData = async (body, endpoint, setter) => {
    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + userToken
        },
        body: JSON.stringify(body)
      };

      const response = await fetch(endpoint, requestOptions);
      if (response.ok && (response.status === 200 || response.status === 201)) {
        const data = await response.json();
        setter(data);
      } else {
        console.error('Error fetching data. Status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const customDimensionFilter = null;
  const endpointStandard = process.env.REACT_APP_NERTHUS_API_URL + '/reports/analyticsReports?type=standard';
  const endpointRealTime = process.env.REACT_APP_NERTHUS_API_URL + '/reports/analyticsReports?type=realtime';
  const startDateFormatted = dates.startDate.toISOString().split('T')[0];
  const endDateFormatted = dates.endDate.toISOString().split('T')[0];

  const fetchUsers30MinAgo = () => {
    fetchData(BodyUsers30MinAgo(startDateFormatted, endDateFormatted), endpointRealTime, setResponseUsers30MinAgo);
  };

  const fetchAnalyticsData = async () => {
    await Promise.all([
      fetchData(BodyViewsByPageTitle(startDateFormatted, endDateFormatted, customDimensionFilter), endpointStandard, setResponseViewsByPageTitle),
      fetchData(BodyUsersByCity(startDateFormatted, endDateFormatted, customDimensionFilter), endpointStandard, setResponseUsersByCity),
      fetchData(BodySessionByChannel(startDateFormatted, endDateFormatted, customDimensionFilter), endpointStandard, setResponseSessionByChannel),
      fetchData(BodyViewsByUserSource(startDateFormatted, endDateFormatted, customDimensionFilter), endpointStandard, setResponseViewsByUserSource),
      fetchData(BodyViewsByPages(startDateFormatted, endDateFormatted, customDimensionFilter), endpointStandard, setResponseViewsByPages),
      fetchData(BodyUsers30MinAgo(startDateFormatted, endDateFormatted), endpointRealTime, setResponseUsers30MinAgo)
    ]);
    setIsLoading(false);
  };

  useEffect(() => {
    if (userToken) {
      fetchAnalyticsData();
    }
  }, [userToken]);

  useEffect(() => {
    const interval = setInterval(fetchUsers30MinAgo, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [userToken]);

  const handlerDates = () => {
    setIsLoading(true);

    setResponseUsersByCity({});
    setResponseViewsByPageTitle({});
    setResponseSessionByChannel({});
    setResponseViewsByUserSource({});
    setResponseViewsByPages({});
    setResponseUsers30MinAgo({});

    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }

    setTimer(
      setTimeout(() => {
        setDates({ startDate: new Date(dates.startDate), endDate: new Date(dates.endDate) });
        fetchAnalyticsData();
      }, 1500)
    );
  };

  useEffect(() => {
    if (responseViewsByPageTitle && responseViewsByPageTitle.data) {
      const chartData = ExtractChartData(responseViewsByPageTitle.data);
      setDataChartViewsByClients(chartData);
    }

    if (responseSessionByChannel && responseSessionByChannel.data) {
      const chartData = ExtractChartData(responseSessionByChannel.data);
      setDataChartUsersByChannel(chartData);
    }

    if (responseViewsByUserSource && responseViewsByUserSource.data) {
      const chartData = ExtractChartData(responseViewsByUserSource.data);
      setDataChartViewsByUserSource(chartData);
    }

    if (responseViewsByPages && responseViewsByPages.data) {
      const chartData = ExtractChartData(responseViewsByPages.data);
      setDataChartViewsByPages(chartData);
    }

    if (responseUsersByCity && responseUsersByCity.data) {
      const chartData = ExtractChartData(responseUsersByCity.data);
      setDataChartUsersByCity(chartData);
    }

    if (responseUsers30MinAgo && responseUsers30MinAgo.data) {
      const chartData = ExtractChartData(responseUsers30MinAgo.data);
      setDataChartUsers30MinAgo(chartData);
    }

  }, [responseViewsByPageTitle, responseSessionByChannel, responseUsersByCity, responseUsers30MinAgo, responseViewsByUserSource, responseViewsByPages]);

  const modalDetail = (codeItem, pathClient) => {
    setShowDetail(true);
    setPathClientDetail(pathClient);
  };

  return (
    <>
      {isLoading ? <Spinner /> : null}
      <Container fluid className='dashboard'>
        <ChartPopup
          show={showChartPopup}
          onHide={() => setShowChartPopup(false)}
          chartData={selectedChartData}
          titleChart={chartPopupTitle}
        />

        <ModalDetail show={showDetail} onHide={() => setShowDetail(false)} pathClient={pathClientDetail} dates={dates} />

        <CustomDatePicker dates={dates} setDates={setDates} handlerDates={handlerDates} />

        <Row>
          <Col md={4} className='sidebar'>
            <Card className='card' border='primary' style={styles.cardContainer}>
              <Card.Header><b>Users 30 Minutes Ago (RealTime)</b>
                <div style={{ float: 'right', width: '30px' }}>
                  <Button
                    className="btn btn-primary"
                    size="sm"
                    title="chart"
                    onClick={() => handleChartButtonClick(dataChartUsers30MinAgo, 'Users 30 Minutes Ago')}
                  >
                    <AiOutlineLineChart title="chart" />
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <div>
                  <DinamicTableAnalytics data={responseUsers30MinAgo.data} />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4} className='sidebar'>
            <Card className='card' border='primary' style={styles.cardContainer}>
              <Card.Header> ViewsByClients
                <div style={{ float: 'right', width: '30px' }}>
                  <Button
                    className="btn btn-primary"
                    size="sm"
                    title="chart"
                    onClick={() => handleChartButtonClick(dataChartViewsByClients, 'Views By Clients')}
                  >
                    <AiOutlineLineChart title="chart" />
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <div>
                  <DinamicTableDetail data={responseViewsByPageTitle.data} modalDetail={modalDetail} />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className='card' border='primary' style={styles.cardContainer}>
              <Card.Header>UsersByCity
                <div style={{ float: 'right', width: '30px' }}>
                  <Button
                    className="btn btn-primary"
                    size="sm"
                    title="chart"
                    onClick={() => handleChartButtonClick(dataChartUsersByCity, 'Users By City')}
                  >
                    <AiOutlineLineChart title="chart" />
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <div>
                  <DinamicTableAnalytics data={responseUsersByCity.data} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>


        <Row>
          <Col md={4} className='sidebar'>
            <Card className='card' border='primary' style={styles.cardContainer}>
              <Card.Header>UsersByChannel
                <div style={{ float: 'right', width: '30px' }}>
                  <Button
                    className="btn btn-primary"
                    size="sm"
                    title="chart"
                    onClick={() => handleChartButtonClick(dataChartUsersByChannel, 'Users By Channel')}
                  >
                    <AiOutlineLineChart title="chart" />
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <div>
                  <DinamicTableAnalytics data={responseSessionByChannel.data} />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4} className='sidebar'>
            <Card className='card' border='primary' style={styles.cardContainer}>
              <Card.Header> UserSource
                <div style={{ float: 'right', width: '30px' }}>
                  <Button
                    className="btn btn-primary"
                    size="sm"
                    title="chart"
                    onClick={() => handleChartButtonClick(dataChartViewsByUserSource, 'Views By User Source')}
                  >
                    <AiOutlineLineChart title="chart" />
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <div>
                  <DinamicTableAnalytics data={responseViewsByUserSource.data} />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4} className='sidebar'>
            <Card className='card' border='primary' style={styles.cardContainer}>
              <Card.Header> Views By Pages
                <div style={{ float: 'right', width: '30px' }}>
                  <Button
                    className="btn btn-primary"
                    size="sm"
                    title="chart"
                    onClick={() => handleChartButtonClick(dataChartViewsByPages, 'Views By Pages')}
                  >
                    <AiOutlineLineChart title="chart" />
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <div>
                  <DinamicTableAnalytics data={responseViewsByPages.data} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default GoogleAnalyticsReport;